/**
 * Observer surfaces (M5): Auction TV and Auction Progress.
 *
 * Both connect to the ring as role 'observer' - no token, and the ring admits
 * observers on the PUBLIC projection only, so nothing here can leak (paddles,
 * bidder ids, reserves never arrive on the wire). Catalog TEXT comes from
 * control-api's public catalog endpoint (also redacted). The ring is the
 * truth for what is happening now; the catalog is decoration.
 *
 *   Auction TV       tv.html?t=<tenant>&sale=<saleSlug>[&ringId=ring-2][&ring=…][&api=…]
 *   Auction Progress progress.html?t=<tenant>&sale=<saleSlug>[&ringId=ring-2][&ring=…][&api=…]
 *
 * ADR-70 cut 5c: a sale running several rings has one screen per ring, picked
 * by engine ring id (?ringId=); without it a screen shows the first ring.
 *
 * One module, two entry points (tv.ts / progress.ts) that call mount() with a
 * renderer. The connection, reconnection, catalog fetch, and public-event
 * bookkeeping are identical; only the DOM differs.
 */

import { ProtocolClient, type Snapshot } from '@hipnumber/protocol-client';
import { runOrdered as runOrderedShared } from '@hipnumber/auction-core';
import type { LotWords } from './lot-term.js';

export type PubLot = {
  id: string;
  hipNumber: string;
  title: string;
  kind: string;
  attributes: Record<string, unknown>;
  runOrder: number | null;
  status: string;
  /** The engine ring id of the ring the lot runs in (cut 5c), null if unplaced. */
  ring?: string | null;
};

export interface ObserverState {
  saleTitle: string;
  /** The sale's product (HIPNUMBER | LOTNUMBER) from the catalog the board already
   *  fetches, so a board names the catalog number in the sale's own word. Null until
   *  the catalog answers, and when it gives none: read as HipNumber, as lotTermFor does. */
  product: string | null;
  lots: PubLot[];
  snap: Snapshot | null;
  /** Bids on the current lot (public BID_ACCEPTED count). */
  bids: number;
  /** Last few sold lots, most recent first. */
  results: { hip: string; title: string; price: number; source: string }[];
  connection: 'connecting' | 'connected' | 'reconnecting' | 'closed';
}

export const usd = (n: number | null | undefined): string =>
  n === null || n === undefined ? '-' : '$' + n.toLocaleString();

/**
 * The last-result line on the TV, in the sale's word for the catalog number.
 * `seen` is a sale this screen watched happen; failing that, `previous` is what
 * the ring says it last disposed (a screen that joined between lots has seen
 * nothing) and `previousHip` is that lot's number. Empty when there is nothing
 * to say yet.
 */
export function lastResultLine(
  words: LotWords,
  seen: { hip: string; price: number } | undefined,
  previous: { outcome: string; price: number | null } | null,
  previousHip: string | null,
): string {
  if (seen) return `Just sold - ${words.cap} ${seen.hip} ${usd(seen.price)}`;
  if (!previous || previousHip === null) return '';
  if (previous.outcome === 'SOLD') return `Just sold - ${words.cap} ${previousHip} ${usd(previous.price)}`;
  // ADR-65: the last word on a lot can be that its sale no longer stands. Never why.
  return `${words.cap} ${previousHip} - ${previous.outcome === 'NO_SALE' ? 'no sale' : previous.outcome === 'VOIDED' ? 'sale voided' : 'passed'}`;
}

export function lotSubtitle(lot: PubLot | undefined): string {
  if (!lot) return '';
  const a = lot.attributes;
  const parts = [a['foalYear'], a['color'], a['sex'], a['heightHands'] ? `${a['heightHands']} hh` : null];
  return parts.filter(Boolean).join(' · ') || lot.kind;
}

/** ADR-64: the one run-order sort (auction-core), shared with GFX and the
 *  clerk console. */
export const runOrdered = (lots: PubLot[]): PubLot[] => runOrderedShared(lots);

/** Public lot-state → the word a spectator understands. */
export const stateLabel: Record<string, string> = {
  UPCOMING: 'UP NEXT',
  OPEN: 'BIDDING',
  FAIR_WARNING: 'GOING ONCE',
  HAMMERED_PENDING: 'HAMMER',
  SOLD: 'SOLD',
  NO_SALE: 'NO SALE',
  PASSED: 'PASSED',
  SCRATCHED: 'SCRATCHED',
};

/** ADR-70 cut 3: the step of the close; mirrors gfx-contract CLOSE_STEP_LABEL. */
export const closeStepLabel: Record<string, string> = {
  GOING_ONCE: 'GOING ONCE',
  GOING_TWICE: 'GOING TWICE',
  LAST_CALL: 'LAST CALL',
};

const LIVE_STATES = new Set(['OPEN', 'FAIR_WARNING', 'HAMMERED_PENDING']);

/**
 * What the board should show right now, resolved robustly. Pure and tested.
 *
 * The observer knows the ring's current lotId (a uuid) and matches it against
 * the PUBLIC catalog. That match can fail - a lot not yet published, or one
 * added on the fly - and before this the board rendered giant placeholders (a
 * 22vh em-dash reads as a stray bar). This decides a clean phase and text:
 *
 *   waiting  no lot on the ring yet → "Waiting for the ring", no hip.
 *   live     a lot is being bid → hip + title (or "On the block" if the
 *            catalog can't resolve it); the money is live.
 *   result   the current lot is sold/passed/scratched or up-next → hip +
 *            title (or "Between lots"); the money is the last result, not a
 *            live ask, and the renderer can dim it.
 */
export interface CurrentView {
  phase: 'waiting' | 'live' | 'result';
  hip: string;       // '' when unknown - the renderer hides the block
  title: string;
  subtitle: string;
  state: string;     // spectator word, '' to hide the badge
  live: boolean;     // money is a live ask (vs a settled result)
}

export function currentView(state: ObserverState): CurrentView {
  const snap = state.snap;
  const lot = state.lots.find((l) => l.id === snap?.lotId);
  const st = snap?.lotState ?? '';
  if (!snap || !snap.lotId) {
    return { phase: 'waiting', hip: '', title: 'Waiting for the ring', subtitle: '', state: '', live: false };
  }
  const hip = lot?.hipNumber ?? '';
  const subtitle = lotSubtitle(lot);
  if (LIVE_STATES.has(st)) {
    // ADR-70 cut 3: a closing lot shows the step called (the snapshot names it).
    const step = st === 'FAIR_WARNING' ? closeStepLabel[String(snap['closeStep'])] : undefined;
    return { phase: 'live', hip, title: lot?.title ?? 'On the block', subtitle, state: step ?? stateLabel[st] ?? st, live: true };
  }
  const resultTitle = lot?.title ?? ({ SOLD: 'Lot sold', NO_SALE: 'No sale', PASSED: 'Passed', SCRATCHED: 'Scratched', UPCOMING: 'Up next' }[st] ?? 'Between lots');
  return { phase: 'result', hip, title: resultTitle, subtitle, state: stateLabel[st] ?? st, live: false };
}

/**
 * Where the control-api lives for a console page. `?api=` overrides; a laptop
 * uses the dev default; a real host maps its first label onto api. on the SAME
 * brand site, so the SameSite=Strict session cookie stays same-site:
 * console.hipnum.com -> api.hipnum.com, console.lotnumber.com ->
 * api.lotnumber.com (ADR-69 section 13). `console.` was missing here until
 * that cut, which sent start/checkout/gate/paper/check-in on console.hipnum.com
 * to their own origin for /auth (a 404); main.ts, annie.ts and displays.ts
 * already mapped it.
 */
export function apiBase(params: URLSearchParams, loc: Pick<Location, 'hostname' | 'origin'> = location): string {
  return (
    params.get('api') ??
    (loc.hostname === 'localhost' || loc.hostname === '127.0.0.1'
      ? 'http://localhost:4100'
      : loc.origin.replace(/^https?:\/\/(console|tv|progress|display|b)\./, (m) => m.replace(/(console|tv|progress|display|b)\./, 'api.')))
  );
}

/** What an observer surface needs to connect, once resolved. The display page
 *  gets these from a device token; tv.html/progress.html from query params. */
export interface ObserverConfig {
  api: string;
  tenant: string;
  saleSlug: string;
  /** Explicit ring override (dev). Harmless on observers - they hold no token. */
  ring?: string;
  /** ADR-70 cut 5c: which ring of a multi-ring sale, by engine ring id. */
  ringId?: string;
}

/** A running observer connection; close() tears the socket down so a display
 *  can switch to a different sale without leaking WebSockets. */
export interface ObserverHandle {
  readonly state: ObserverState;
  close(): void;
}

/**
 * Connect one observer surface for a resolved sale. Fetches the public catalog
 * (which also carries the live ring endpoint), then connects as 'observer'.
 * `render(state)` runs on every change. Returns a handle whose state the caller
 * can read (for a heartbeat) and whose close() ends the connection.
 */
export function run(config: ObserverConfig, render: (state: ObserverState) => void): ObserverHandle {
  const state: ObserverState = { saleTitle: '', product: null, lots: [], snap: null, bids: 0, results: [], connection: 'connecting' };
  const lotById = (id: string | null | undefined) => state.lots.find((l) => l.id === id);
  let client: ProtocolClient | null = null;
  let closed = false;

  void (async () => {
    let ringUrl = config.ring ?? '';
    try {
      const r = await fetch(`${config.api}/t/${encodeURIComponent(config.tenant)}/catalog/${encodeURIComponent(config.saleSlug)}`);
      if (r.ok) {
        const j = (await r.json()) as { sale: { title: string; product?: unknown; ringEndpoint?: string | null; rings?: { ringId: string; name: string; endpoint: string | null }[] }; lots: PubLot[] };
        // The word for the catalog number rides on this response; an older API sends none.
        state.product = typeof j.sale.product === 'string' ? j.sale.product : null;
        const rings = j.sale.rings ?? [];
        const chosen = config.ringId ? rings.find((x) => x.ringId === config.ringId) : rings[0];
        if (config.ringId && rings.length && !chosen) {
          state.saleTitle = `This sale has no ring ${config.ringId}`;
          state.connection = 'closed';
          render(state);
          return;
        }
        // One ring per screen: its name in the title, its lots, its address.
        const several = rings.length > 1 && chosen;
        state.saleTitle = several ? `${j.sale.title} · ${chosen.name}` : j.sale.title;
        state.lots = several ? j.lots.filter((l) => l.ring === chosen.ringId) : j.lots;
        if (!ringUrl) ringUrl = (chosen?.endpoint ?? j.sale.ringEndpoint) ?? '';
        render(state);
      }
    } catch {
      /* catalog is decoration; the ring is the truth */
    }
    if (closed) return; // caller switched away before the catalog resolved
    client = new ProtocolClient({
      url: ringUrl || 'ws://localhost:4000',
      actorId: 'observer',
      role: 'observer',
      onSnapshot: (s) => {
        state.snap = s;
        render(state);
      },
      onEvent: (ev) => {
        switch (ev['type']) {
          case 'BID_ACCEPTED':
            state.bids++;
            break;
          case 'LOT_OPENED':
          case 'CURRENT_LOT_SET':
          // ADR-70 cut 6d: the lot left the ring; nothing is selling.
          case 'LOT_REMOVED_FROM_RING':
            state.bids = 0;
            break;
          case 'LOT_SOLD': {
            const lot = lotById(ev['lotId'] as string);
            state.results.unshift({
              hip: lot?.hipNumber ?? String(ev['lotId']),
              title: lot?.title ?? '',
              price: ev['price'] as number,
              source: ev['bidSource'] === 'FLOOR' ? 'floor' : 'online',
            });
            state.results = state.results.slice(0, 12);
            break;
          }
          default:
            break;
        }
        render(state);
      },
      onStatus: (c) => {
        state.connection = c;
        render(state);
      },
    });
  })();

  render(state);
  return {
    state,
    close() {
      closed = true;
      client?.close();
    },
  };
}

/** Wire an observer surface from URL params (tv.html / progress.html). */
export function mount(render: (state: ObserverState) => void): void {
  const params = new URLSearchParams(location.search);
  const t = params.get('t');
  const saleSlug = params.get('sale');
  if (!t || !saleSlug) {
    render({ saleTitle: 'need ?t=<tenant>&sale=<sale>', product: null, lots: [], snap: null, bids: 0, results: [], connection: 'closed' });
    return;
  }
  const ring = params.get('ring');
  const ringId = params.get('ringId');
  // When this observer runs inside a HipNumber Display shell (an iframe), report
  // real ring health up to the shell so its heartbeat can tell the operator the
  // WebSocket is actually live - the signal Amazon's device heartbeat can't give.
  const framed = typeof window !== 'undefined' && window.parent !== window;
  run({ api: apiBase(params), tenant: t, saleSlug, ...(ring ? { ring } : {}), ...(ringId ? { ringId } : {}) }, (s) => {
    render(s);
    if (framed) {
      try {
        window.parent.postMessage(
          { source: 'hipnumber-display', health: { connection: s.connection, ringConnected: s.connection === 'connected', saleSlug, lotState: s.snap?.lotState ?? null } },
          '*',
        );
      } catch {
        /* health is best-effort */
      }
    }
  });
}
